import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, X, Image as ImageIcon } from 'lucide-react'

const ImageUpload = ({ onImageSelect, label = "マシン画像" }) => {
  const [preview, setPreview] = useState(null)

  const resizeImage = (file) => {
    return new Promise((resolve) => {
      const img = new Image()
      img.src = URL.createObjectURL(file)
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        const size = 400

        canvas.width = size
        canvas.height = size

        // Calculate crop
        let sourceX = 0
        let sourceY = 0
        let sourceWidth = img.width
        let sourceHeight = img.height

        if (img.width > img.height) {
          sourceWidth = img.height
          sourceX = (img.width - img.height) / 2
        } else {
          sourceHeight = img.width
          sourceY = (img.height - img.width) / 2
        }

        ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, size, size)

        canvas.toBlob((blob) => {
          if (blob) {
            const resizedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            })
            resolve(resizedFile)
          } else {
            resolve(file) // Fallback
          }
        }, 'image/jpeg', 0.8) // Compress to 0.8 quality
      }
      img.onerror = () => resolve(file)
    })
  }

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0]
    if (file) {
      const resizedFile = await resizeImage(file)
      const objectUrl = URL.createObjectURL(resizedFile)
      setPreview(objectUrl)
      onImageSelect(resizedFile)
    }
  }, [onImageSelect])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.png', '.jpg', '.webp']
    },
    maxFiles: 1
  })

  const removeImage = (e) => {
    e.stopPropagation()
    setPreview(null)
    onImageSelect(null)
  }

  return (
    <div className="w-full">
      <label className="block text-sm font-bold text-gray-400 mb-1">{label}</label>
      <div
        {...getRootProps()}
        className={`relative border-2 border-dashed rounded-xl p-4 transition-all duration-200 cursor-pointer flex flex-col items-center justify-center min-h-[160px]
          ${isDragActive ? 'border-orange-500 bg-orange-500/10' : 'border-gray-700 bg-[#2c2c2e] hover:border-gray-500'}
        `}
      >
        <input {...getInputProps()} />

        {preview ? (
          <div className="relative w-full h-full flex justify-center">
            <img
              src={preview}
              alt="Preview"
              className="max-h-[200px] rounded-lg object-contain"
            />
            <button
              onClick={removeImage}
              className="absolute top-[-10px] right-[-10px] bg-red-500 rounded-full p-1 hover:bg-red-600 transition-colors shadow-lg"
            >
              <X size={16} color="white" />
            </button>
          </div>
        ) : (
          <div className="text-center text-gray-400">
            <div className="flex justify-center mb-3">
              {isDragActive ? (
                <Upload size={32} className="text-orange-500" />
              ) : (
                <ImageIcon size={32} />
              )}
            </div>
            <p className="text-sm font-medium">
              {isDragActive ? "ドロップして追加" : "画像をドラッグ＆ドロップ"}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              またはクリックして選択
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ImageUpload
